import VueI18n from 'vue-i18n';
import MessageFormat from '@messageformat/core';
import { http } from '@pendo/http';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import Vue from 'vue';

/* eslint-disable no-prototype-builtins */
class CustomFormatter {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this._locale = options.locale || 'en';
    this._formatter = new MessageFormat(this._locale, {
      returnType: 'values'
    });
    this._caches = Object.create(null);
  }
  set locale(locale) {
    this._locale = locale;
  }
  interpolate(message, values) {
    this._formatter = new MessageFormat(this._locale, {
      returnType: 'values'
    });
    let fn = this._caches[message];
    if (!fn) {
      fn = this._formatter.compile(message, this._locale);
      this._caches[message] = fn;
    }
    if (values !== null && values !== undefined) {
      /**
       * Component interpolation in the following format passes `values` as an Array-like `Object`
       *
       * ```vue
       * <i18n path="terms" for="tos">
       *   I agree with the <a href="/terms">{{ $t('tos') }}</a>
       * </i18n>
       * ```
       *
       * VNodes _may_ be found when using interpolated values with either the deprecated `places` **or** the new slot
       * syntax
       */
      const valuesEntries = Object.entries(values);
      const isNonSlotInterpolation = valuesEntries.some(_ref => {
        let [key, value] = _ref;
        return value && value.hasOwnProperty('componentOptions') && isNaN(Number.parseFloat(key)) === false;
      });
      if (isNonSlotInterpolation) {
        /**
         * In this case, we iterate over the VNode list and preserve **only** VNodes with tags and return them, wrapped
         * in a list.
         *
         * Rationale: If text nodes are kept, the positional lookup from vue-i18n will fail, rendering the first
         * child again (i.e `I agree with the, I agree with the`)
         */
        values = valuesEntries.map(_ref2 => {
          let [, vnode] = _ref2;
          if (vnode.tag !== undefined) {
            return [vnode];
          }
          return false;
        }).filter(Boolean);
      }
    }
    const result = fn(values).map(val => {
      if (!Array.isArray(val)) {
        return val;
      }
      const hasVNodes = val.some(part => part.hasOwnProperty('componentOptions'));
      if (!hasVNodes) {
        return val.join('');
      }
      return val;
    });
    return result;
  }
}

function sanitizeKey() {
  let originalString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return originalString.replace(/\s\s*/g, '_').replace(/[^\\0-9A-Za-z_]/g, '_');
}
function sanitizeKeys(source) {
  if (isArray(source)) {
    return source.map(sourceProp => {
      return sanitizeKeys(sourceProp);
    });
  }
  if (isObject(source)) {
    const clonedSource = {};
    Object.keys(source).forEach(prop => {
      const newKey = sanitizeKey(prop);
      clonedSource[newKey] = sanitizeKeys(source[prop]);
    });
    return clonedSource;
  }
  return source;
}
function splitKeyPath(key) {
  const pathNodes = key.split(/(?<=[a-zA-Z])\.(?=[a-zA-Z])(?!io)/g);
  return pathNodes;
}

/* eslint-disable */
const {
  VUE_APP_I18N_LOCALE = 'en',
  VUE_APP_I18N_SUPPORTED_LOCALE = 'en,de,ja'
} = process.env;
const TranslationPlugin = {
  get defaultLocale() {
    return VUE_APP_I18N_LOCALE;
  },
  get supportedLocales() {
    return VUE_APP_I18N_SUPPORTED_LOCALE.split(',');
  },
  setI18nLocaleInServices(_ref) {
    let {
      locale,
      i18n
    } = _ref;
    i18n.locale = locale;
    i18n.formatter.locale = locale;
    document.documentElement.setAttribute('lang', locale);
    if (http) {
      // Set headers if http util initialized by consuming repo
      http.defaults.headers.common['AcceptLanguage'] = locale;
    }
    return locale;
  },
  async changeLocale(_ref2) {
    let {
      locale,
      msgs,
      loadLocaleFileFn,
      i18n
    } = _ref2;
    if (!i18n) {
      return Promise.reject(new Error('PendoI18n instance not set'));
    }
    if (!TranslationPlugin.isLocaleSupported(locale)) {
      return Promise.reject(new Error('Locale not supported'));
    }
    if (i18n.locale === locale) {
      return Promise.resolve(locale);
    }
    if (!msgs && loadLocaleFileFn) {
      // If messages not preloaded, lazy-load locale only as needed
      msgs = await loadLocaleFileFn(locale); // Promise.resolve
    }

    const sanitizedMsgs = sanitizeKeys(msgs);
    i18n.setLocaleMessage(locale, sanitizedMsgs.default || sanitizedMsgs);
    return TranslationPlugin.setI18nLocaleInServices({
      locale,
      i18n
    });
  },
  isLocaleSupported(locale) {
    return TranslationPlugin.supportedLocales.includes(locale);
  }
};

const DEFAULT_LOCALE = TranslationPlugin.defaultLocale;
class PendoI18n extends VueI18n {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const {
      locale = DEFAULT_LOCALE,
      fallbackLocale = DEFAULT_LOCALE,
      messages = {
        [DEFAULT_LOCALE]: {}
      },
      ...rest
    } = options;
    const formatter = new CustomFormatter({
      i18n_locale: locale
    });
    super({
      locale,
      fallbackLocale,
      messages: {
        ...Object.freeze(sanitizeKeys(messages))
      },
      formatter,
      ...rest
    });
  }
  changeLocale(newLocaleSettings) {
    TranslationPlugin.changeLocale({
      ...newLocaleSettings,
      i18n: this
    });
  }
  i18n_t(originalString) {
    let args = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    if (originalString === undefined || originalString == null || originalString === '') {
      return originalString;
    }

    // Find contextual key paths but intentionally ignore sentence punctuation and ellipses
    const pathParts = splitKeyPath(originalString);
    const originalKey = pathParts.pop();
    const sanitizedKey = sanitizeKey(originalKey);
    const path = pathParts.length ? `${pathParts.join('.')}.` : '';
    return args == null ? this.t(path + sanitizedKey) : this.t(path + sanitizedKey, args);
  }
}
function install(_vue) {
  _vue.use(VueI18n);
  _vue.prototype.$t = function (key, values, path) {
    return this.$i18n.i18n_t(key, values, path);
  };
  _vue.prototype.$locale = function () {
    return this.$i18n.locale;
  };
}
PendoI18n.install = install;

const i18nGlobalTypes = {
  storybookLocale: {
    name: 'storybookLocale',
    description: 'Internationalization locale',
    defaultValue: 'en',
    toolbar: {
      icon: 'globe',
      items: [{
        value: 'en',
        title: 'English'
      }, {
        value: 'ja',
        title: '日本語'
      }, {
        value: 'de',
        title: 'Deutsch'
      }],
      dynamicTitle: true
    }
  }
};
const locale = Vue.observable({
  value: null
});
const withLocale = (story, context) => {
  locale.value = context.globals.storybookLocale;
  const {
    messages,
    defaultLocale
  } = context.parameters.i18n;
  Vue.use(PendoI18n);
  const i18n = new PendoI18n({
    locale: defaultLocale,
    messages
  });
  return {
    template: '<story/>',
    i18n,
    props: {
      storybookLocale: {
        type: String,
        default: 'en'
      },
      messages: {
        type: Object,
        default: messages
      }
    },
    created() {
      this.$watch(() => {
        i18n.changeLocale({
          locale: locale.value,
          msgs: messages[locale.value]
        });
      });
    }
  };
};
const i18nDecorators = [withLocale];

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    i18nGlobalTypes: i18nGlobalTypes,
    i18nDecorators: i18nDecorators
});

const formatter = new MessageFormat('en');
const mock$T = function () {
  let originalString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  let args = arguments.length > 1 ? arguments[1] : undefined;
  if (originalString === undefined || originalString == null || originalString === '') {
    return originalString;
  }
  const pathParts = originalString.split(/(?<=[a-zA-Z])\.(?=[a-zA-Z])(?!io)/g);
  const originalKey = pathParts.pop();
  if (args && formatter) {
    const msg = formatter.compile(originalKey);
    return msg(args);
  }
  return originalKey;
};

export { PendoI18n, index as StorybookUtils, mock$T };
